import { create } from 'zustand';
import {
  IAuthPayload,
  LoginPayload,
  ResetPassword,
  SignUpPayload,
} from '../models/auth.model';
import errorHandler from '../utils/error-handler.util';
import { IApiResponse } from '../models/resource.model';
import createAxiosInstance from '../services/global/axiosInstance';
import { storageUtil } from '../utils/storage.util';

const axiosInstance = createAxiosInstance();

interface IAuthStore {
  signedUpEmail: string;
  loading: boolean;
  setLoading: (status: boolean) => void;
  setSignedUpEmail: (email: string) => void;
  login: (loginPayload: LoginPayload) => Promise<IApiResponse>;
  logout: () => void;
  callRefreshToken: (token: string) => Promise<IApiResponse>;
  signup: (signupPayload: SignUpPayload) => Promise<IApiResponse>;
  verifyEmail: (token: string | null) => Promise<IApiResponse>;
  validateEmail: (email: string) => Promise<IApiResponse>;
  resendVerificationEmail: (auth: string) => Promise<IApiResponse>;
  forgotPassword: (email: string) => Promise<IApiResponse>;
  resetPassword: (payload: ResetPassword) => Promise<IApiResponse>;
  saveAuthStateToStorage: (payload: IAuthPayload) => void;
  updateAuthStateInStorage: (payload: IAuthPayload) =>void;
  loadAuthStateFromStorage: () => IAuthPayload | null;
  removeAuthStateFromStorage: () => void;
  addKybStatusToLocalStorage: (kybStatus: number) => void;
  isAuthenticated: () => boolean;
  kybStatus: () => number;
  getAccessToken: () => string | null;
}

const useAuthStore = create<IAuthStore>((set, get) => {
  return {
    signedUpEmail: '',
      loading: false,
    setLoading: (status: boolean) => {
    set({ loading: status });
  },
    setSignedUpEmail: (email: string) => {
    set({ signedUpEmail: email });
  },
    saveAuthStateToStorage: (payload: IAuthPayload) => {
    storageUtil.setItem('authState', payload);
  },
    loadAuthStateFromStorage: () => {
      return storageUtil.getItem<IAuthPayload>('authState');
    },
    updateAuthStateInStorage: (payload: IAuthPayload) => {
      const oldStorageVals = storageUtil.getItem<IAuthPayload>('authState')
      storageUtil.setItem('authState', {...oldStorageVals, ...payload});
  },
    removeAuthStateFromStorage: () => {
    storageUtil.removeItem('authState');
  },
    isAuthenticated: () => {
    const authState: any = get().loadAuthStateFromStorage();
    return authState && authState?.accessToken;
  },
    kybStatus: () => {
    const authState: any = get().loadAuthStateFromStorage();
    return authState && authState?.kybStatus;
  },
    getAccessToken: () => {
    const authState: any = get().loadAuthStateFromStorage();
    return authState ? authState?.accessToken : null;
  },
    login: async (loginPayload: LoginPayload) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.post('/auth/login', loginPayload);
      get().saveAuthStateToStorage(response.data.value);
      set({ loading: false });
      return response.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
    logout: () => {
    get().removeAuthStateFromStorage();
    window.location.href = '/login';
  },
    addKybStatusToLocalStorage: (kybStatus: number) => {
    const authToken = get().loadAuthStateFromStorage();
    get().saveAuthStateToStorage({
      ...authToken,
      kybStatus,
    } as IAuthPayload);
  },
    callRefreshToken: async (token: string) => {
    try {
      const response = await axiosInstance.put('/auth/refresh-token', {
        refreshToken: token,
      });
      return response.data;
    } catch (err: any) {
      errorHandler(err);
    }
  },
    signup: async (signupPayload: SignUpPayload) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.post('/auth/create', signupPayload);
      set({ loading: false });
      return response?.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
    }
  },
    verifyEmail: async (token: string | null) => {
    try {
      const response = await axiosInstance.put(`/auth/verify-email/${token}`);
      return response?.data?.value;
    } catch (err: any) {
      errorHandler(err);
    }
  },
    validateEmail: async (email: string) => {
    try {
      const response = await axiosInstance.put(`/auth/validate-email/${email}`);
      return response?.data?.value;
    } catch (err: any) {
      errorHandler(err);
    }
  },
    resendVerificationEmail: async (auth: string) => {
    try {
      const response = await axiosInstance.put(
        `/auth/resend-email-verification/${auth}`,
      );
      return response?.data?.value;
    } catch (err: any) {
      errorHandler(err);
    }
  },
    forgotPassword: async (email: string) => {
    try {
      const response = await axiosInstance.put(
        `/auth/forgot-password/${email}`,
      );
      return response?.data?.value;
    } catch (err: any) {
      errorHandler(err);
    }
  },
    resetPassword: async (payload: ResetPassword) => {
    try {
      const response = await axiosInstance.post(
        `/auth/reset-password`,
        payload,
      );
      return response?.data;
    } catch (err: any) {
      errorHandler(err);
    }
  },
  }
});

export default useAuthStore;
